import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogContent,
  Slide
} from '@mui/material';
import {
  HowToVote,
  EmojiEvents,
  Schedule,
  VerifiedUser,
  LocalAtm,
  TrendingUp,
  Star,
  History,
  Casino,
  Celebration
} from '@mui/icons-material';
import axios from 'axios';
import '../styles/Vote.css';
import { API_URL } from '../config';
import VoteX2Banner from '../components/VoteX2Banner';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcNFwktAAAAAKbvR33vzPo9xFQNMS5ocxfImKMZ';

const REMINDER_OPTIONS = [
  { value: 0,  label: 'Dès que dispo' },
  { value: 5,  label: '+ 5 min' },
  { value: 10, label: '+ 10 min' },
  { value: 30, label: '+ 30 min' },
];

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Vote = () => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [stats, setStats] = useState(null);
  const [topVoters, setTopVoters] = useState([]);
  const [topVotersLoading, setTopVotersLoading] = useState(true);
  const [voteHistory, setVoteHistory] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });
  const [countdown, setCountdown] = useState(0);
  const [canVote, setCanVote] = useState(true);
  const [user, setUser] = useState(null);
  const [pendingToken, setPendingToken] = useState(
    localStorage.getItem('pendingVoteToken') || null
  );
  const [pendingVoteUrl, setPendingVoteUrl] = useState(null);
  const [notifStatus, setNotifStatus] = useState('unknown');
  const [reminderOffset, setReminderOffset] = useState(
    parseInt(localStorage.getItem('voteReminderOffset') || '0', 10)
  );
  const [ogrinesBalance, setOgrinesBalance] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  
  const [jackpotWin, setJackpotWin] = useState({ show: false, amount: 0 });
  const [jackpotInfo, setJackpotInfo] = useState({ current: 0, lastWinner: null });
  
  const [voteX2Config, setVoteX2Config] = useState({ active: false, endAtMs: null });
  
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setNotifStatus('unsupported');
      return;
    }
    const perm = Notification.permission;
    if (perm === 'denied') { setNotifStatus('denied'); return; }
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setNotifStatus(sub ? 'subscribed' : perm === 'granted' ? 'granted' : 'default');
      });
    }).catch(() => setNotifStatus('default'));
  }, []);

  const enableNotifications = async () => {
    if (!user || !user.id) { showAlert('error', 'Connectez-vous pour activer les notifications'); return; }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setNotifStatus('denied'); showAlert('warning', 'Notifications refusées.'); return; }
      const vapidRes = await axios.get(`${API_URL}/vote/vapid-key`);
      const publicKey = vapidRes.data.publicKey;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      await axios.post(`${API_URL}/vote/push-subscribe`, {
        accountId: user.id, subscription: sub.toJSON(), reminderOffsetMinutes: reminderOffset
      });
      setNotifStatus('subscribed');
      const opt = REMINDER_OPTIONS.find(o => o.value === reminderOffset);
      showAlert('success', `🔔 Notifications activées ! Rappel : ${opt?.label ?? 'dès que dispo'}.`);
    } catch (err) {
      showAlert('error', 'Impossible d\'activer les notifications.');
    }
  };

  const disableNotifications = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${API_URL}/vote/push-unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setNotifStatus('default');
      showAlert('info', 'Notifications désactivées.');
    } catch (err) {
      showAlert('error', 'Erreur lors de la désactivation.');
    }
  };

  const handleReminderChange = async (newOffset) => {
    setReminderOffset(newOffset);
    localStorage.setItem('voteReminderOffset', String(newOffset));
    if (notifStatus === 'subscribed' && user) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await axios.post(`${API_URL}/vote/push-subscribe`, {
            accountId: user.id, subscription: sub.toJSON(), reminderOffsetMinutes: newOffset
          });
          const opt = REMINDER_OPTIONS.find(o => o.value === newOffset);
          showAlert('success', `⏰ Rappel mis à jour : ${opt?.label ?? newOffset + ' min'}`);
        }
      } catch (err) {
        showAlert('error', 'Erreur lors de la mise à jour du rappel.');
      }
    }
  };

  useEffect(() => {
    if (pendingToken) localStorage.setItem('pendingVoteToken', pendingToken);
    else localStorage.removeItem('pendingVoteToken');
  }, [pendingToken]);

  useEffect(() => {
    loadTopVoters();
    loadJackpotInfo();
    loadVoteX2Status();
  }, []);

  useEffect(() => {
    if (user && user.id) {
      loadVoteData();
      loadVoteHistory();
      loadOgrinesBalance();
    }
  }, [user]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canVote) {
      setCanVote(true);
    }
  }, [countdown, canVote]);

  const loadVoteX2Status = async () => {
    try {
      const res = await axios.get(`${API_URL}/vote/x2-status`);
      if (res.data.success) {
        setVoteX2Config({
          active: res.data.data.active,
          endAtMs: res.data.data.end_at_ms
        });
      }
    } catch (e) {
      console.error('Erreur chargement statut X2:', e);
    }
  };

  const loadJackpotInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/vote/jackpot-info`);
      if (res.data.success) {
        setJackpotInfo({
          current: res.data.current_jackpot || 0,
          lastWinner: res.data.last_winner || null
        });
      }
    } catch (e) {
      console.error('Erreur chargement infos jackpot:', e);
    }
  };

  const loadOgrinesBalance = async () => {
    if (!user || !user.id) return;
    const token = localStorage.getItem('sessionToken');
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/account/profile/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.ogrines !== undefined) setOgrinesBalance(res.data.ogrines);
    } catch (e) {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.ogrines !== undefined) setOgrinesBalance(parsed.ogrines);
      }
    }
  };

  const loadVoteData = async () => {
    if (!user || !user.id) return;
    try {
      const response = await axios.get(`${API_URL}/vote/stats/${user.id}`);
      if (response.data.success) {
        const statsData = response.data.data;
        setStats(statsData);
        setCanVote(statsData.next_vote_available);
        if (!statsData.next_vote_available) {
          setCountdown(statsData.next_vote_seconds);
          setPendingToken(null);
        }
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const loadTopVoters = async () => {
    setTopVotersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/vote/top-voters?limit=10`);
      if (response.data.success) setTopVoters(response.data.data);
    } catch (error) {
      console.error('Erreur chargement top voteurs:', error);
    } finally {
      setTopVotersLoading(false);
    }
  };

  const [historyLimit, setHistoryLimit] = useState(50);
  const loadVoteHistory = async (limit = 50) => {
    if (!user || !user.id) return;
    try {
      const response = await axios.get(`${API_URL}/vote/history/${user.id}?limit=${limit}`);
      if (response.data.success) {
        setVoteHistory(response.data.data);
        setHistoryLimit(limit);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };

  const confirmVote = async () => {
    if (!pendingToken) {
      showAlert('error', 'Aucun vote en attente de confirmation');
      return;
    }
    setVerifying(true);
    try {
      const response = await axios.get(`${API_URL}/vote/verify/${pendingToken}`, {
        params: { accountId: user?.id }
      });
      
      if (response.data.success) {
        const voteData = response.data.data;
        if (voteData.voted_at) {
          
          if (voteData.jackpot_won) {
            setJackpotWin({ show: true, amount: voteData.jackpot_amount });
          } else {
            showAlert('success', response.data.message || `🎉 Vote validé ! Vous avez reçu ${voteData.ogrines_amount} Ogrines`);
          }

          setPendingToken(null);
          setPendingVoteUrl(null);
          
          if (user) {
            try {
              const token = localStorage.getItem('sessionToken');
              const profileRes = await axios.get(`${API_URL}/account/profile/${user.id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (profileRes.data && profileRes.data.ogrines !== undefined) {
                const stored = localStorage.getItem('user');
                if (stored) {
                  const parsed = JSON.parse(stored);
                  parsed.ogrines = profileRes.data.ogrines;
                  localStorage.setItem('user', JSON.stringify(parsed));
                }
              }
            } catch (e) {
              const stored = localStorage.getItem('user');
              if (stored && voteData.ogrines_amount) {
                const parsed = JSON.parse(stored);
                const totalAmount = (voteData.ogrines_amount) + (voteData.jackpot_won ? voteData.jackpot_amount : 0);
                parsed.ogrines = (parsed.ogrines || 0) + totalAmount;
                localStorage.setItem('user', JSON.stringify(parsed));
              }
            }
            window.dispatchEvent(new Event('storage'));
          }
          await loadVoteData();
          await loadTopVoters();
          await loadVoteHistory();
          await loadOgrinesBalance();
          await loadJackpotInfo(); 
          if (voteData.next_vote_seconds) {
            setCountdown(voteData.next_vote_seconds);
            setCanVote(false);
          }
        } else {
          showAlert('warning', 'Vote pas encore enregistré sur serveur-prive.net. Veuillez patienter et réessayer.');
        }
      } else if (response.data.expired || response.data.cooldown) {
        setPendingToken(null);
        showAlert('warning', response.data.message);
        if (response.data.next_vote_seconds) {
          setCountdown(response.data.next_vote_seconds);
          setCanVote(false);
        } else {
          await loadVoteData();
        }
      } else if (response.data.pending) {
        showAlert('info', response.data.message || 'Vous n\'avez pas encore voté. Cliquez sur "Voter", effectuez votre vote, puis revenez confirmer.');
      }
    } catch (error) {
      if (error.response?.status >= 400 && error.response?.status < 500) setPendingToken(null);
      showAlert('error', error.response?.data?.message || 'Erreur lors de la vérification du vote.');
    } finally {
      setVerifying(false);
    }
  };

  const handleVote = async () => {
    if (!user || !user.id) { showAlert('error', 'Vous devez être connecté pour voter'); return; }
    if (!captchaToken) { showAlert('error', 'Veuillez valider le CAPTCHA avant de voter.'); return; }
    if (!canVote) { showAlert('error', `Vous devez attendre ${formatTime(countdown)} avant de voter à nouveau`); return; }

    const voteWindow = window.open('', '_blank');
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const response = await axios.post(`${API_URL}/vote/generate-otp`, {
        accountId: user.id, captchaToken
      }, { headers: { 'Authorization': `Bearer ${sessionToken}` } });

      if (response.data.success) {
        const { vote_url: voteUrl, token, message } = response.data.data;

        if (response.data.auto_validated) {
          if (voteWindow && !voteWindow.closed) voteWindow.close();
          const voteData = response.data.data;
          setCaptchaToken(null);
          if (recaptchaRef.current) recaptchaRef.current.reset();
          
          if (voteData.jackpot_won) {
            setJackpotWin({ show: true, amount: voteData.jackpot_amount });
          } else {
            showAlert('success', response.data.message || `🎉 Vote validé ! Vous avez reçu ${voteData.ogrines_amount} Ogrines`);
          }

          setPendingToken(null);
          setPendingVoteUrl(null);
          
          if (user) {
            try {
              const tkn = localStorage.getItem('sessionToken');
              const profileRes = await axios.get(`${API_URL}/account/profile/${user.id}`, { headers: tkn ? { Authorization: `Bearer ${tkn}` } : {} });
              if (profileRes.data && profileRes.data.ogrines !== undefined) {
                const stored = localStorage.getItem('user');
                if (stored) {
                  const parsed = JSON.parse(stored);
                  parsed.ogrines = profileRes.data.ogrines;
                  localStorage.setItem('user', JSON.stringify(parsed));
                }
              }
            } catch (e) {
              const stored = localStorage.getItem('user');
              if (stored && voteData.ogrines_amount) {
                const parsed = JSON.parse(stored);
                const totalAmount = (voteData.ogrines_amount) + (voteData.jackpot_won ? voteData.jackpot_amount : 0);
                parsed.ogrines = (parsed.ogrines || 0) + totalAmount;
                localStorage.setItem('user', JSON.stringify(parsed));
              }
            }
            window.dispatchEvent(new Event('storage'));
          }
          await loadVoteData();
          await loadTopVoters();
          await loadVoteHistory();
          await loadOgrinesBalance();
          await loadJackpotInfo(); 
          if (voteData.next_vote_seconds) {
            setCountdown(voteData.next_vote_seconds);
            setCanVote(false);
          }
          return;
        }

        setCaptchaToken(null);
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setPendingToken(token);
        
        const isSimulation = voteUrl.includes('localhost');
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

        if (isSimulation) {
          if (voteWindow && !voteWindow.closed) voteWindow.close();
          showAlert('info', message);
        } else if (isMobile) {
          if (voteWindow && !voteWindow.closed) voteWindow.close();
          setPendingVoteUrl(voteUrl);
        } else {
          if (voteWindow && !voteWindow.closed) voteWindow.location.href = voteUrl;
          else window.open(voteUrl, '_blank');
          showAlert('success', 'Page de vote ouverte dans un nouvel onglet.');
        }
      } else {
        if (voteWindow && !voteWindow.closed) voteWindow.close();
      }
    } catch (error) {
      if (voteWindow && !voteWindow.closed) voteWindow.close();
      if (error.response?.status === 401 && error.response?.data?.session_expired) {
        localStorage.removeItem('user');
        localStorage.removeItem('sessionToken');
        window.location.href = `/login${error.response.data.reason === 'ip_changed' ? '?ip_changed=1' : ''}`;
        return;
      }
      showAlert('error', error.response?.data?.message || 'Erreur lors de la génération du token de vote');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'info', message: '' }), 5000);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('fr-FR') + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const voteX2Active = voteX2Config.active && voteX2Config.endAtMs > Date.now();
  const baseVoteReward = stats?.tier?.ogrines ?? 100;
  const effectiveVoteReward = voteX2Active ? baseVoteReward * 2 : baseVoteReward;

  const pulseAnimation = `
    @keyframes pulse-jackpot {
      0% { transform: scale(1); filter: brightness(1); }
      50% { transform: scale(1.03); filter: brightness(1.2); }
      100% { transform: scale(1); filter: brightness(1); }
    }
  `;

  return (
    <Box className="vote-container">
      <style>{pulseAnimation}</style>
      
      <Dialog
        open={jackpotWin.show}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setJackpotWin({ show: false, amount: 0 })}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
            color: '#fff',
            textAlign: 'center',
            borderRadius: 4,
            boxShadow: '0 0 40px rgba(255, 215, 0, 0.6)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 6 }}>
          <Celebration sx={{ fontSize: 80, mb: 2, color: '#fff' }} />
          <Typography variant="h3" sx={{ fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            JACKPOT !
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'medium' }}>
            Incroyable ! Tu viens de faire tomber la cagnotte commune !
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            +{jackpotWin.amount} <img src="/assets/images/1.png" alt="og" className="og-icon" style={{width: 40, height: 40, marginLeft: 12}} />
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{ mt: 5, background: '#fff', color: '#FF8C00', fontWeight: 'bold', fontSize: '1.2rem', '&:hover': { background: '#f5f5f5' } }}
            onClick={() => setJackpotWin({ show: false, amount: 0 })}
          >
            Récupérer mon butin
          </Button>
        </DialogContent>
      </Dialog>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            <HowToVote sx={{ fontSize: 60, verticalAlign: 'middle', mr: 2 }} />
            Votez pour Nexus
          </Typography>
          <Typography variant="h6" sx={{ color: '#B0B0C0' }}>
            Soutenez le serveur et gagnez des Ogrines !
          </Typography>
        </Box>

        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 140, 0, 0.1) 100%)',
            border: '2px solid rgba(255, 215, 0, 0.5)',
            borderRadius: 4,
            mb: 5,
            boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, right: 0, background: '#FF8C00', color: '#fff', px: 3, py: 0.5, borderBottomLeftRadius: 16, fontWeight: 'bold', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
            Seras-tu le prochain?
          </Box>
          
          <CardContent sx={{ p: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between' }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, md: 0 } }}>
              <Avatar sx={{ width: 80, height: 80, background: 'linear-gradient(135deg, #FFD700, #FF8C00)', boxShadow: '0 4px 15px rgba(255, 140, 0, 0.5)', mr: 3 }}>
                <Casino sx={{ fontSize: 40, color: '#fff' }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 2 }}>
                  La Cagnotte Commune
                </Typography>
                <Typography variant="body2" sx={{ color: '#C9CED6', mt: 0.5 }}>
                  Chaque vote a une chance de faire tomber le pot !
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center', animation: 'pulse-jackpot 2.5s infinite' }}>
              <Typography variant="h2" sx={{ fontWeight: '900', color: '#FFD700', textShadow: '0 0 20px rgba(255, 215, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {jackpotInfo.current} <img src="/assets/images/1.png" alt="og" className="og-icon" style={{ width: 45, height: 45, marginLeft: 15 }} />
              </Typography>
            </Box>

            <Box sx={{ textAlign: { xs: 'center', md: 'right' }, mt: { xs: 3, md: 0 }, borderLeft: { md: '1px solid rgba(255, 215, 0, 0.2)' }, pl: { md: 4 } }}>
              <Typography variant="caption" sx={{ color: '#B0B0C0', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                Dernier Grand Gagnant
              </Typography>
              {jackpotInfo.lastWinner ? (
                <>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                    <Celebration sx={{ color: '#FFD700', fontSize: 20, mr: 1 }} />
                    {jackpotInfo.lastWinner.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FF8C00', fontWeight: 'bold' }}>
                    A remporté {jackpotInfo.lastWinner.amount} Ogrines !
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: '#C9CED6', fontStyle: 'italic' }}>
                  En attente du premier chanceux...
                </Typography>
              )}
            </Box>

          </CardContent>
        </Card>

        {!user && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            Vous devez être connecté pour voter. <a href="/login" style={{ color: '#B67A3C', fontWeight: 'bold' }}>Se connecter</a>
          </Alert>
        )}

        {alert.show && (
          <Alert severity={alert.type} sx={{ mb: 4 }}>
            {alert.message}
          </Alert>
        )}
        {voteX2Active && <VoteX2Banner endDateMs={voteX2Config.endAtMs} sx={{ mb: 4 }} />}

        <Grid container spacing={4}>
          
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(182, 122, 60, 0.1) 0%, rgba(167, 174, 184, 0.05) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(182, 122, 60, 0.2)',
                borderRadius: 4,
                height: '100%'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 100, height: 100, margin: '0 auto 20px',
                      background: 'linear-gradient(135deg, #B67A3C, #A7AEB8)',
                      boxShadow: '0 8px 32px rgba(182, 122, 60, 0.4)'
                    }}
                  >
                    <HowToVote sx={{ fontSize: 60 }} />
                  </Avatar>

                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Voter maintenant
                  </Typography>

                  <Typography variant="body1" sx={{ color: '#B0B0C0', mb: 3 }}>
                    Chaque vote vous rapporte{' '}
                    <strong style={{ color: '#C9CED6' }}>
                      {effectiveVoteReward} <img src="/assets/images/1.png" alt="og" className="og-icon" />
                    </strong>
                  </Typography>

                  {user && canVote && !pendingToken && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        theme="dark"
                        onChange={(token) => setCaptchaToken(token)}
                        onExpired={() => setCaptchaToken(null)}
                        onErrored={() => setCaptchaToken(null)}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={!user || !canVote || loading || pendingToken || (!captchaToken && canVote && !pendingToken)}
                    onClick={handleVote}
                    sx={{
                      py: 2, fontSize: '1.2rem', fontWeight: 'bold',
                      background: (user && canVote && !pendingToken)
                        ? 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)' : 'rgba(100, 100, 100, 0.3)',
                      '&:hover': { background: (user && canVote && !pendingToken) ? 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)' : undefined },
                      mb: 2
                    }}
                  >
                    {loading ? ( <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : !user ? ( 'Connectez-vous pour voter'
                    ) : pendingToken ? ( 'Vote en attente de confirmation'
                    ) : canVote ? ( <> <HowToVote sx={{ mr: 1 }} /> Voter pour Nexus </>
                    ) : ( <> <Schedule sx={{ mr: 1 }} /> Prochain vote dans {formatTime(countdown)} </> )}
                  </Button>

                  {pendingToken && (
                    <>
                      {pendingVoteUrl && (
                        <Button
                          variant="contained" size="large" fullWidth component="a" href={pendingVoteUrl} target="_blank" rel="noopener noreferrer"
                          onClick={() => setPendingVoteUrl(null)}
                          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)', '&:hover': { background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)' }, mb: 2 }}
                        >
                          <HowToVote sx={{ mr: 1 }} /> Voter sur serveur-prive.net
                        </Button>
                      )}
                      <Button
                        variant="outlined" size="large" fullWidth disabled={verifying} onClick={confirmVote}
                        sx={{ py: 2, fontSize: '1.1rem', fontWeight: 'bold', borderColor: '#C9CED6', color: '#C9CED6', borderWidth: 2, '&:hover': { borderColor: '#C9CED6', background: 'rgba(201, 206, 214, 0.1)', borderWidth: 2 }, mb: 2 }}
                      >
                        {verifying ? ( <> <CircularProgress size={24} sx={{ color: '#C9CED6', mr: 1 }} /> Vérification en cours... </> ) : ( <> <VerifiedUser sx={{ mr: 1 }} /> Confirmer le vote </> )}
                      </Button>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Après avoir voté sur serveur-prive.net, cliquez sur "Confirmer le vote" pour recevoir vos Ogrines.
                      </Alert>
                      <Button
                        variant="text" size="small" fullWidth disabled={verifying}
                        onClick={() => { setPendingToken(null); setPendingVoteUrl(null); showAlert('info', 'Vote annulé.'); }}
                        sx={{ color: '#888', fontSize: '0.85rem', mb: 1 }}
                      >
                        Annuler / Régénérer un nouveau vote
                      </Button>
                    </>
                  )}

                  {!canVote && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={stats ? ((5400 - countdown) / 5400) * 100 : 0}
                        sx={{ height: 8, borderRadius: 4, backgroundColor: 'rgba(182, 122, 60, 0.1)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #B67A3C, #C9CED6)' } }} />
                      <Typography variant="caption" sx={{ color: '#B0B0C0', mt: 1, display: 'block' }}>
                        Cooldown : 1h30 entre chaque vote
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 3, borderColor: 'rgba(182, 122, 60, 0.2)' }} />

                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <EmojiEvents sx={{ color: '#C9CED6', mr: 1 }} /> Récompenses
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalAtm sx={{ color: '#C9CED6', mr: 2 }} />
                      <Typography>{effectiveVoteReward} <img src="/assets/images/1.png" alt="og" className="og-icon" /> / vote</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Casino sx={{ color: '#00BCD4', mr: 2 }} />
                      <Typography>1 Jeton de vote tous les 10 votes</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingUp sx={{ color: '#4CAF50', mr: 2 }} />
                      <Typography>+1 Vote pour le classement</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Star sx={{ color: '#C9CED6', mr: 2 }} />
                      <Typography>Soutenir le serveur</Typography>
                    </Box>
                  </Box>

                  {stats?.tier && (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2, borderColor: 'rgba(182, 122, 60, 0.2)' }} />
                      <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                        <EmojiEvents sx={{ color: '#B67A3C', mr: 1 }} /> Palier
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 2, background: 'linear-gradient(135deg, rgba(182,122,60,0.25), rgba(167,174,184,0.15))', border: '1px solid rgba(182,122,60,0.4)', mb: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{stats.tier.emoji} {stats.tier.name}</Typography>
                        <Typography variant="body2" sx={{ color: '#C9CED6', fontWeight: 'bold' }}>{stats.tier.ogrines} pts / vote</Typography>
                      </Box>
                      {stats.tier.next_tier_votes !== null && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#B0B0C0' }}>Progression ce mois</Typography>
                            <Typography variant="caption" sx={{ color: '#B0B0C0' }}>{stats.monthly_votes} / {stats.tier.next_tier_votes} votes</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(100, (stats.monthly_votes / stats.tier.next_tier_votes) * 100)} sx={{ height: 8, borderRadius: 4, background: 'rgba(182,122,60,0.15)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #B67A3C, #C9CED6)' } }} />
                        </>
                      )}
                      {stats.tier.next_tier_votes === null && (
                        <Typography variant="body2" sx={{ color: '#C9CED6', textAlign: 'center', mt: 1 }}>👑 Tu es Seigneur du Nexus — Maître du Temps !</Typography>
                      )}
                    </Box>
                  )}
                </Box>
                
                <Divider sx={{ my: 3, borderColor: 'rgba(182, 122, 60, 0.2)' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ color: notifStatus === 'subscribed' ? '#4CAF50' : '#B67A3C' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff' }}>{notifStatus === 'subscribed' ? '🔔 Notifications actives' : '🔕 Notifier quand vote dispo'}</Typography>
                        <Typography variant="caption" sx={{ color: '#B0B0C0' }}>{notifStatus === 'subscribed' ? 'Alerte PC & mobile à chaque recharge' : notifStatus === 'denied' ? 'Notifications bloquées dans le navigateur' : notifStatus === 'unsupported' ? 'Non supporté par ce navigateur' : 'Recevez une alerte automatique'}</Typography>
                      </Box>
                    </Box>
                    {notifStatus !== 'unsupported' && notifStatus !== 'denied' && (
                      notifStatus === 'subscribed' ? (
                        <Button size="small" variant="outlined" onClick={disableNotifications} sx={{ borderColor: '#ef5350', color: '#ef5350', '&:hover': { background: 'rgba(239,83,80,0.1)', borderColor: '#ef5350' } }}>Désactiver</Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={enableNotifications} disabled={!user} sx={{ background: 'linear-gradient(135deg,#B67A3C,#A7AEB8)', '&:hover': { background: 'linear-gradient(135deg,#8D5A2A,#8D5A2A)' } }}>Activer</Button>
                      )
                    )}
                  </Box>
                  {notifStatus !== 'unsupported' && notifStatus !== 'denied' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: '#B0B0C0', whiteSpace: 'nowrap' }}>Rappel :</Typography>
                      <ToggleButtonGroup exclusive value={reminderOffset} onChange={(_, val) => { if (val !== null) handleReminderChange(val); }} size="small" sx={{ '& .MuiToggleButton-root': { color: 'rgba(255,255,255,0.45)', borderColor: 'rgba(182,122,60,0.25)', fontSize: '0.72rem', py: 0.4, px: 1.2, textTransform: 'none' }, '& .Mui-selected': { color: '#D5A777 !important', background: 'rgba(182,122,60,0.18) !important', borderColor: 'rgba(182,122,60,0.55) !important' } }}>
                        {REMINDER_OPTIONS.map(opt => <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>)}
                      </ToggleButtonGroup>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
                        <Card sx={{ background: 'rgba(20, 25, 45, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(182, 122, 60, 0.2)', borderRadius: 4, mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <VerifiedUser sx={{ mr: 1, color: '#B67A3C' }} /> Mes Statistiques
                </Typography>
                {stats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(182, 122, 60, 0.2), rgba(167, 174, 184, 0.1))' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#B67A3C' }}>{stats.total_votes}</Typography>
                        <Typography variant="body2" sx={{ color: '#B0B0C0' }}>Votes Total</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(201, 206, 214, 0.2), rgba(255, 193, 7, 0.1))' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#C9CED6' }}>{stats.ogrines_earned}</Typography>
                        <Typography variant="body2" sx={{ color: '#B0B0C0' }}>Ogrines Gagnés</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(30, 136, 229, 0.1))' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196F3' }}>{stats.monthly_votes}</Typography>
                        <Typography variant="body2" sx={{ color: '#B0B0C0' }}>Votes ce mois-ci</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.2), rgba(0, 151, 167, 0.1))' }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#00BCD4' }}>{stats.votes_avant_jeton}</Typography>
                        <Typography variant="body2" sx={{ color: '#B0B0C0' }}>Vote(s) avant 1 Jeton</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(201, 206, 214, 0.15), rgba(255, 160, 0, 0.08))', border: '1px solid rgba(201, 206, 214, 0.25)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#C9CED6' }}>{ogrinesBalance !== null ? ogrinesBalance : (user?.ogrines ?? '—')}</Typography>
                          <img src="/assets/images/1.png" alt="og" className="og-icon" style={{ width: 28, height: 28 }} />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#B0B0C0' }}>Ogrines disponibles</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : ( <Box sx={{ textAlign: 'center', py: 4 }}> <CircularProgress sx={{ color: '#B67A3C' }} /> </Box> )}
              </CardContent>
            </Card>
            <Card sx={{ background: 'rgba(20, 25, 45, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(182, 122, 60, 0.2)', borderRadius: 4, mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <History sx={{ mr: 1, color: '#2196F3' }} /> Mon Historique
                </Typography>
                {voteHistory.length > 0 ? (
                  <>
                    <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                      {voteHistory.map((vote, index) => (
                        <Box key={vote.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, mb: 1, borderRadius: 2, background: 'rgba(182, 122, 60, 0.05)', border: '1px solid rgba(182, 122, 60, 0.1)' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Vote #{stats?.total_votes - index}</Typography>
                            <Typography variant="caption" sx={{ color: '#B0B0C0' }}>{formatDate(vote.voted_at)}</Typography>
                          </Box>
                          <Chip label={<>+{vote.ogrines_amount} <img src="/assets/images/1.png" alt="og" className="og-icon" style={{width:13,height:13,verticalAlign:'middle'}} /></>} size="small" sx={{ background: 'linear-gradient(135deg, #C9CED6, #B67A3C)', color: '#000', fontWeight: 'bold' }} />
                        </Box>
                      ))}
                    </Box>
                    {stats && stats.total_votes > historyLimit && (
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button variant="outlined" size="small" onClick={() => loadVoteHistory(Math.min(historyLimit + 50, 500))} sx={{ borderColor: 'rgba(182,122,60,0.4)', color: '#B0B0C0' }}>Charger {Math.min(50, stats.total_votes - historyLimit)} votes de plus</Button>
                      </Box>
                    )}
                  </>
                ) : ( <Box sx={{ textAlign: 'center', py: 4 }}> <Typography sx={{ color: '#B0B0C0' }}>Aucun vote pour le moment</Typography> </Box> )}
              </CardContent>
            </Card>
            <Card sx={{ background: 'rgba(20, 25, 45, 0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(182, 122, 60, 0.2)', borderRadius: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center' }}>
                  <EmojiEvents sx={{ mr: 1, color: '#C9CED6' }} /> Top Voteurs — {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
                </Typography>
                {topVotersLoading ? ( <Box sx={{ textAlign: 'center', py: 4 }}> <CircularProgress sx={{ color: '#B67A3C' }} /> </Box>
                ) : topVoters.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#B0B0C0', fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ color: '#B0B0C0', fontWeight: 'bold' }}>Joueur</TableCell>
                          <TableCell align="right" sx={{ color: '#B0B0C0', fontWeight: 'bold' }}>Votes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topVoters.map((voter, index) => (
                          <TableRow key={voter.guid} sx={{ '&:hover': { background: 'rgba(182, 122, 60, 0.1)' } }}>
                            <TableCell>
                              {index === 0 && <Chip label="🥇" size="small" sx={{ background: '#C9CED6' }} />}
                              {index === 1 && <Chip label="🥈" size="small" sx={{ background: '#C0C0C0' }} />}
                              {index === 2 && <Chip label="🥉" size="small" sx={{ background: '#CD7F32' }} />}
                              {index > 2 && <Typography>{index + 1}</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{voter.pseudo}</TableCell>
                            <TableCell align="right" sx={{ color: '#B67A3C', fontWeight: 'bold' }}>{voter.vote_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : ( <Box sx={{ textAlign: 'center', py: 4 }}> <Typography sx={{ color: '#B0B0C0' }}>Aucun voteur ce mois-ci</Typography> </Box> )}
              </CardContent>
            </Card>

          </Grid>
        </Grid>
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, background: 'linear-gradient(135deg, #B67A3C 0%, #C9CED6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            ⏳ Strates du Temps
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#B0B0C0', mb: 4 }}>
            Les paliers se réinitialisent au début de chaque mois
          </Typography>
          <Grid container spacing={2}>
            {[
              { name: 'Nomade Temporel',    emoji: '⏳', min: 0,   max: 19,  ogrines: 600  },
              { name: 'Voyageur des Failles', emoji: '🌀', min: 20,  max: 59,  ogrines: 800  },
              { name: 'Fractureur',           emoji: '⚡', min: 60,  max: 129, ogrines: 1000 },
              { name: 'Gardien Nexus',        emoji: '🔮', min: 130, max: 219, ogrines: 1200 },
              { name: 'Seigneur du Nexus',    emoji: '👑', min: 220, max: null, ogrines: 1600 },
            ].map((tier) => {
              const isActive = stats?.tier?.name === tier.name;
              return (
                <Grid item xs={12} sm={6} md={2.4} key={tier.name}>
                  <Card sx={{ background: isActive ? 'linear-gradient(135deg, rgba(182,122,60,0.35), rgba(201,206,214,0.15))' : 'rgba(20,25,45,0.6)', border: isActive ? '2px solid #B67A3C' : '1px solid rgba(182,122,60,0.2)', borderRadius: 3, textAlign: 'center', p: 2, transform: isActive ? 'scale(1.04)' : 'none', transition: 'all 0.2s' }}>
                    <Typography variant="h3">{tier.emoji}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>{tier.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#B0B0C0', display: 'block' }}>{tier.max !== null ? `${tier.min}–${tier.max} votes` : `${tier.min}+ votes`}</Typography>
                    <Typography variant="body1" sx={{ color: '#C9CED6', fontWeight: 'bold', mt: 1 }}>{tier.ogrines} pts / vote</Typography>
                    {isActive && <Chip label="Palier actuel" size="small" sx={{ mt: 1, background: '#B67A3C', color: 'white', fontWeight: 'bold' }} />}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Alert severity="info" sx={{ maxWidth: 800, margin: '0 auto' }}>
            <Typography variant="body2">
              💡 <strong>Comment ça marche ?</strong> Cliquez sur "Voter pour Nexus", vous serez redirigé vers serveur-prive.net. Une fois votre vote validé, vos Ogrines seront automatiquement ajoutés à votre compte !
            </Typography>
          </Alert>
        </Box>
      </Container>
    </Box>
  );
};

export default Vote;